import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import classNames from "classnames";
import React, { useEffect, useState } from "react";

interface EditableLeaseCompFieldProps {
  rule: LeaseCompFieldRule;
  fieldValue: string | undefined;
  onEdit: (rule: LeaseCompFieldRule) => void;
  onValueChange: (value: string, fieldKey: string) => void;
}

const EditableLeaseCompField = ({
  rule,
  fieldValue,
  onEdit,
  onValueChange,
}: EditableLeaseCompFieldProps) => {
  const [value, setValue] = useState(fieldValue || "");

  // Sync internal state when the saved value changes (e.g. after a successful
  // PATCH, or when the parent re-renders with data from the RTK cache).
  useEffect(() => {
    setValue(fieldValue || "");
  }, [fieldValue]);

  const handleFocus = () => {
    // When the user focuses a "—" field, clear it so they can type immediately
    // without having to manually delete the placeholder dash.
    if (value === "\u2014") {
      setValue("");
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const trimmedValue = e.target.value.trim();

    // If the user focused a "—" field without entering a value, restore the
    // dash display and skip the PATCH call entirely.
    if (trimmedValue === "" && fieldValue === "\u2014") {
      setValue("\u2014");
      return;
    }

    if (trimmedValue !== fieldValue) {
      onValueChange(e.target.value, rule.keyName);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setValue(e.target.value);
  };

  return (
    <div className="flex items-center gap-x-1">
      <EmeraldInputV2
        type="text"
        label={rule.displayName || ""}
        value={value}
        size="small"
        className={classNames("without-label flex-15", {
          boldLabel: rule.isActive,
        })}
        isValid={true}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      <EmeraldIcon
        icon="edit"
        type="filled"
        variant="icon"
        onClick={() => onEdit(rule)}
        className="cursor-pointer"
      />
    </div>
  );
};

export default EditableLeaseCompField;
